// Lowering a PROCESS FICHA to the questions a capture asks (C1).
//
// La idea no cambió: un proceso ya declara qué datos lleva (su ficha) y qué
// etapa los obtiene, y eso es exactamente lo que un flujo de captura pregunta.
// Escribirlo a mano en el editor, nodo por nodo, es copiar a mano una lista que
// el sistema ya tiene, y las dos copias se separan al primer cambio de ficha.
//
// WHY IT MOVED HERE (Process-Compiled Agent, Wave 2). This lowering was born in
// `packages/api/src/skills/capture-flow-generator.ts` because only the HTTP
// route needed it. The compiler needs the SAME lowering — a connected process
// with a `captura` stage compiles to a callable capture whose asks follow that
// process's ficha (D15) — and `@vysto/shared` cannot import `@vysto/api`. A
// second copy here is precisely the drift this cycle exists to end, so the pure
// part moved down into shared and the API file re-exports it: one
// implementation, and `capture-flow-generator.test.ts` still asserts it.
//
// LO QUE NO SE REDISEÑA. El cierre de tres ramas del flujo de captura de fábrica
// (creada → confirmar → fin.handoff auto; rechazo → avisar_asesor → fin.handoff;
// cualquier otro caso → fin.ok) es un lock de Juan del 2026-08-22 y lo arma
// `buildCaptureFlow`. Aquí solo cambian las preguntas, el embudo y la etapa.
//
// PURE + web-safe: no React, no DB, no node builtins.

import {
  captureCategoryLabelFor,
  captureFlowNameFor,
  type CaptureFlowAgenda,
  type CaptureFlowOptions,
  type CaptureFlowQuestion,
  generatedAgendaConfirm,
  generatedCaptureConfirm,
} from './capture-flow.js'
import type { AgentTerminal } from './diff.js'
import type { CompilerStageBehavior } from './types.js'
import {
  askExtractForDataType,
  type FichaDataType,
  type FieldOptionsSource,
} from '../bot-config/field-kind-map.js'

/** `skills.name` es VARCHAR(80): el nombre del proceso puede ser más largo. */
const FLOW_NAME_MAX = 80

/** La clave del dato de INTERÉS del giro, cuyo valor vive en la COLUMNA
 *  `leads.specialty_label` y nunca en la bolsa `campos`.
 *
 *  Es la misma clave que `INTEREST_KEY` del catálogo de prospectos
 *  (packages/api/src/leads/lead-field-catalog.ts), y NO una segunda copia: ese
 *  archivo la re-exporta desde aquí. Vive en `@vysto/shared` porque el
 *  compilador la necesita y `@vysto/shared` no puede importar `@vysto/api`. */
export const INTEREST_FIELD_KEY = 'interes'

/** Un dato de la ficha del proceso, como lo necesita el generador. */
export interface FichaField {
  key: string
  label: string
  dataType: FichaDataType
  options: string[] | null
  /** DE DÓNDE SALEN esas opciones (D21-1). `null`, ausente o `lista` = las
   *  escribió el cliente. Viaja hasta el `extract` del `ask` (D21-3) para que
   *  la pregunta publicada lleve su fuente y el runtime la pueda volver a
   *  resolver al contestar. */
  optionsSource?: FieldOptionsSource | null
  /** CÓMO SE PREGUNTA ESTE DATO EN ESTE PROCESO (F4-4). Es configuración del
   *  cliente, guardada en el renglón de la ficha (`pipeline_fields`), no una
   *  segunda copia de la definición: el mismo dato se pide legítimamente
   *  distinto en dos embudos.
   *
   *  `null`, ausente o en blanco significa "usa la frase generada", nunca "falta
   *  un dato": así renombrar el dato sigue moviendo su pregunta sola. Por eso es
   *  opcional, y por eso la generada queda BYTE POR BYTE igual que antes cuando
   *  nadie escribió la suya. */
  prompt?: string | null
}

/** Una COLUMNA del proceso, como el plan la lee. Es la misma forma que
 *  `CompilerStage` menos lo que el plan no mira, y `behavior` es el MISMO tipo:
 *  qué corre en la columna lo dice `behavior`, nunca `kind` (D1/D2). */
export interface CaptureFlowPlanStage {
  key: string
  name: string
  position: number
  behavior: CompilerStageBehavior
  /** Lo que la etapa EXIGE para entrar. Sin estas respuestas `capturar_lead`
   *  rechaza la captura, así que el flujo generado siempre las pregunta. */
  requiredFields: readonly string[]
  /** Lo que la etapa declara que OBTIENE (narrativo, CP3 L). Es la lista que
   *  dice qué se pregunta AQUÍ y no en otra columna. */
  gatheredFields: readonly string[]
}

export interface CaptureFlowPlanInput {
  pipelineName: string
  /** La clave del proceso. Es la clave de la opción del Descubrir: ya es un slug
   *  y ya es única por espacio, así que dos procesos nunca chocan entre sí. */
  pipelineKey: string
  /** CÓMO RECONOCE EL AGENTE ESTE PROCESO (F4-5): la etiqueta EFECTIVA de su
   *  opción del menú, ya resuelta por quien leyó la configuración
   *  (`process_connections.entry_label` ?? `pipelines.entry_label`).
   *
   *  Ausente o `null` = derívala del nombre, que es lo que el plan hacía antes
   *  de que esto fuera configurable. Llega YA RESUELTA, un solo valor: el plan
   *  no lee tablas y no puede ser el segundo lugar donde se decide la
   *  precedencia. Sin esto, el compilador y el generador de la pantalla de
   *  Procesos escribirían dos etiquetas distintas para la misma opción y el
   *  detector de deriva reclamaría un desfase fantasma en cada proceso con
   *  etiqueta propia. */
  entryLabel?: string | null
  /** QUÉ SIGNIFICA ESTE PROCESO (F5-2): `pipelines.meaning`, tal cual.
   *
   *  Ausente o `null` = no se escribió ninguno y la PISTA del clasificador es
   *  la etiqueta efectiva, que es lo que el clasificador leía antes de F5-2.
   *  Llega en crudo (y no ya coalescido) porque el plan tiene que poder decir
   *  las dos cosas: con qué enruta el agente (`optionHint`, siempre presente) y
   *  si el cliente escribió un significado (`meaning`, que es lo que la vista
   *  previa nombra como configuración suya). */
  meaning?: string | null
  /** Las columnas del proceso. Pueden venir en cualquier orden: el plan las
   *  ordena con el orden TOTAL del compilador (`position`, y la clave para
   *  desempatar), que es el mismo que el emisor lee. */
  stages: readonly CaptureFlowPlanStage[]
  /** La ficha ACTIVA del proceso, en su orden.
   *
   *  EL NOMBRE VIENE AQUÍ DENTRO (F5-1). Hasta F4-7 la pregunta del nombre era
   *  un par de banderas del proceso (`asks_name` / `name_question_prompt`) que
   *  este plan anteponía a la ficha. Juan, CP-4': "debemos de poder hacer lo
   *  mismo con nombre que con otros datos… no podría ni elegir dónde quiero que
   *  se pregunte". Así que dejó de ser un caso aparte: `nombre` es un renglón de
   *  la ficha como cualquier otro — con su frase en `prompt`, en la posición que
   *  el cliente le dio y en la columna que lo declara — y lo único especial que
   *  conserva es que NOMBRA LA TARJETA (viaja en el argumento `nombre` de
   *  `capturar_lead`, nunca dentro de `campos`), exactamente como `interes`
   *  viaja en el suyo. */
  ficha: readonly FichaField[]
}

/** Un TRAMO: la parte de la captura que aterriza en UNA etapa. Las preguntas se
 *  nombran por su ÍNDICE en la lista plana del plan, no por copia: la lista
 *  plana es la que leen la vista previa, la fusión y el detector de deriva, y
 *  dos copias de las mismas preguntas serían la deriva de siempre. */
export interface CaptureStagePlan {
  stageKey: string
  stageName: string
  /** Índices en `CaptureFlowPlan.questions` que ESTE tramo pregunta. Vacío en un
   *  tramo que sólo adelanta la tarjeta (la etapa no declara nada, o todo lo que
   *  declara ya se preguntó antes). */
  questionIndexes: number[]
  /** Lo que la etapa EXIGE, restringido a la ficha y en su orden. Se lista
   *  aunque otro tramo anterior ya lo haya preguntado: es lo que esta columna
   *  pide para dejar entrar la tarjeta, no lo que este tramo pregunta. */
  requiredKeys: string[]
  /** QUÉ CORRE EN ESTE TRAMO (D22-6). Sale del `behavior` de la columna, y
   *  viaja en el plan porque el emisor y la vista previa lo necesitan dicho: un
   *  tramo de `agenda` no sólo pregunta, RESERVA. */
  kind: 'captura' | 'agenda'
  /** Con qué agenda ESTE tramo, resuelto POR CONVENCIÓN desde la ficha y las
   *  preguntas del plan. Presente sólo en un tramo de `agenda`. */
  agenda?: CaptureStageAgendaPlan
}

/** Lo que una etapa de `agenda` necesita saber de la ficha para poder reservar,
 *  ya resuelto: el emisor no adivina claves. */
export interface CaptureStageAgendaPlan extends CaptureFlowAgenda {
  intake: NonNullable<CaptureFlowAgenda['intake']>
}

/** LAS CLAVES RESERVADAS DE LA TOMA DE DATOS DE LA VISITA (D22-6).
 *
 *  La reserva puede guardar en la cita el motivo, si es la primera visita y la
 *  cobertura, y `book_appointment` las lee con esos nombres. No se inventan ni se
 *  preguntan de más: un proceso las manda SÓLO si su ficha las lleva y su
 *  captura las pregunta. Un proceso que no las tiene reserva igual; esos
 *  argumentos son opcionales en la herramienta. */
export const AGENDA_INTAKE_KEYS = {
  reason: 'motivo',
  firstVisit: 'primera_vez',
  insurer: 'cobertura',
} as const

export interface CaptureFlowPlan {
  /** Nombre bajo el que se publica el flujo llamado. Es la identidad del flujo:
   *  volver a generar el mismo proceso publica una versión nueva de ESTE, no un
   *  segundo flujo (`skills` se agrupa por (bot_id, name)). */
  flowName: string
  /** La clave de la categoría: la del proceso, tal cual. Es lo que hace la
   *  generación idempotente y lo que el guardia de choque compara. */
  optionKey: string
  /** La etiqueta de la categoría: una FRASE, no el nombre del proceso. Es lo
   *  único que el clasificador lee para enrutar. Desde F4-5 es la que el cliente
   *  escribió (`entryLabel`) cuando escribió una, y la derivada
   *  (`captureCategoryLabelFor`) cuando no. */
  optionLabel: string
  /** EL SIGNIFICADO DEL PROCESO (F5-2): la PISTA que la opción del Descubrir le
   *  da al clasificador, o `null` cuando el cliente no escribió ninguno.
   *
   *  `null` NO se rellena con una frase derivada, y ahí está la diferencia con
   *  `optionLabel`. La caída existe, pero vive donde de verdad ocurre: una
   *  opción sin pista la clasifica el modelo leyendo su ETIQUETA, que es lo
   *  único que leía antes de F5-2. Escribir aquí la etiqueta otra vez sería
   *  decir dos veces lo mismo en el prompt y cambiaría la opción emitida de
   *  todo proceso que no configuró nada. */
  meaning: string | null
  /** El id del nodo `call` que el Descubrir gana. Misma forma que el scaffold. */
  callNodeId: string
  questions: CaptureFlowQuestion[]
  /** CUÁL de las preguntas es el nombre, o `null` cuando el proceso no lo
   *  pregunta (F4-7). Viaja en el plan porque el emisor lo necesita EXPLÍCITO:
   *  con la pregunta quitada, la primera pregunta es un dato de la ficha, y un
   *  emisor que lo leyera por posición mandaría ese dato como nombre de la
   *  tarjeta y lo sacaría de `campos`, perdiéndolo en silencio. */
  nameVar: string | null
  /** Cómo se LLAMA cada pregunta, en el mismo orden que `questions`. La vista
   *  previa las nombra una por una en vez de contarlas: "3 preguntas" no le dice
   *  a nadie cuáles son, y un Dueño que acaba de escribir una lista de 4
   *  opciones lee ese 3 como un error (F44a). La etiqueta es la de la ficha, la
   *  misma palabra que el cliente escribió en Datos del proceso. */
  questionLabels: string[]
  funnel: string
  /** La PRIMERA etapa que la captura compila: donde nace la tarjeta.
   *
   *  Sigue siendo un campo plano y sigue significando lo mismo que antes de los
   *  tramos, porque es lo que `captureStageKeyOf` lee del PRIMER `act` de un
   *  flujo vivo. Compararlo contra el último tramo marcaría como deriva todo
   *  flujo por etapas recién generado. */
  stage: string
  stageName: string
  /** Los TRAMOS, en el orden en que la conversación los recorre. Con una sola
   *  etapa declarante trae un solo tramo, que es el flujo de siempre. */
  stages: CaptureStagePlan[]
  /** La etapa del ÚLTIMO tramo: donde queda la tarjeta cuando la captura
   *  termina completa. Con un solo tramo es igual a `stage`. */
  landingStage: string
  landingStageName: string
  /** Ninguna etapa de captura del proceso declaró un dato de la ficha, así que
   *  se pregunta la ficha COMPLETA en la primera (regla de PROCESO, no de
   *  etapa). Se dice en el plan porque la pantalla explica por qué el flujo
   *  pregunta todo. */
  wholeFichaFallback: boolean
  pipelineName: string
  confirmText: string
  /** A DÓNDE VA LA CONVERSACIÓN CUANDO LA CAPTURA TERMINA BIEN (F4-6).
   *
   *  Derivada de las columnas del proceso, nunca guardada: es la misma cuenta
   *  que compila el Agente (`terminalAfterCapture`). Viaja en el plan porque
   *  TRES cosas la necesitan juntas y tienen que coincidir o la conversación se
   *  queda en silencio: el cierre del flujo llamado, la última frase de la
   *  confirmación, y la arista de regreso del `call` en el orquestador. */
  terminal: AgentTerminal
}

/** LA CLAVE DEL DATO QUE NOMBRA LA TARJETA.
 *
 *  Es la variable donde aterriza el nombre, el argumento `nombre` de
 *  `capturar_lead`, y desde F5-1 también la clave del dato de plataforma que la
 *  biblioteca siembra en todo espacio (`nombre`, etiqueta "Nombre"). Las tres
 *  cosas son una sola por construcción: la pregunta que el agente hace, la
 *  respuesta que bautiza la tarjeta y el renglón de la ficha donde se configura
 *  son el mismo hecho.
 *
 *  SIGUE RESERVADA contra los datos que crea el CLIENTE (`RESERVED_LEAD_KEYS`,
 *  packages/api/src/routes/me/fields.ts): la fila sembrada entra por el
 *  provisionamiento, que no pasa por esa ruta, igual que `interes`. La reserva
 *  no existe para que el dato no exista — existe para que el cliente no pueda
 *  crear un homónimo que choque con el argumento de la herramienta. */
export const NAME_FIELD_KEY = 'nombre'

/** La frase con la que se pregunta el nombre cuando el cliente no escribió la
 *  suya. Es la misma del flujo de fábrica: una sola voz.
 *
 *  UNA SOLA CASA (F4-7), igual que `promptForField`: la pantalla de Procesos la
 *  enseña como placeholder del renglón del nombre, así que la importa de aquí
 *  en vez de volver a escribirla. */
export const DEFAULT_NAME_QUESTION_PROMPT = '¿Con quién tengo el gusto?'

/** El texto que se genera para preguntar un dato cuando el cliente NO escribió
 *  el suyo. La frase SIGUE AL TIPO del dato, porque preguntar por una fecha, por
 *  una cantidad y por un sí o un no no son la misma pregunta.
 *
 *  POR QUÉ CAMBIÓ (F6-4). Hasta aquí había UNA plantilla para todo lo que no
 *  fuera el nombre o el interés — `¿Me compartes tu {etiqueta}?` — y la etiqueta
 *  entraba pegada a un "tu" singular. En la transcripción de Juan eso produjo
 *  "¿Me compartes tu tipos de estudio?" (plural metido en una frase singular) y
 *  "¿Me compartes tu cita estudio confirmada?" (un booleano preguntado como si
 *  fuera un dato que se comparte), a lo que el prospecto contestó "eh?".
 *
 *  DOS REGLAS, y las dos existen para que NINGUNA etiqueta pueda romper la
 *  gramática:
 *    1. la etiqueta va ENTRECOMILLADA y tal como el cliente la escribió, así que
 *       la frase no la conjuga ni la concuerda: es una cita, no un sustantivo de
 *       la oración;
 *    2. un booleano dice CÓMO se contesta ("sí o no"), que es lo que faltaba
 *       para que la pregunta fuera contestable.
 *  El interés y el nombre conservan su frase de siempre, byte por byte: las dos
 *  son oraciones que YA funcionan ("¿Qué especialidad buscas?") y quedaron
 *  probadas en la misma transcripción.
 *
 *  UNA SOLA CASA (F4-4). La pantalla de Procesos la muestra como placeholder y
 *  como ayuda ("si lo dejas vacío, el agente pregunta: …"), así que la web
 *  importa ESTA función desde `@vysto/shared/compiler` en vez de volver a
 *  escribir la plantilla: dos copias de la frase harían que la ayuda prometiera
 *  una pregunta distinta de la que el agente hace. Por eso el tipo entra en la
 *  firma: quien enseña la frase ya tiene el renglón de la ficha completo. */
export function promptForField(field: Pick<FichaField, 'key' | 'label' | 'dataType'>): string {
  // EL NOMBRE SE PREGUNTA POR SU FRASE, NO POR SU ETIQUETA (F5-1). Desde que es
  // un renglón de la ficha, la plantilla de un dato cualquiera le habría puesto
  // "¿Me compartes tu nombre?" y habría cambiado, en silencio y en todo proceso
  // vivo, la frase con la que el agente abre la captura desde siempre. La de
  // fábrica es la misma de antes, byte por byte, y sale de la MISMA constante
  // que la pantalla enseña como placeholder.
  if (field.key === NAME_FIELD_KEY) return DEFAULT_NAME_QUESTION_PROMPT
  if (field.key === INTEREST_FIELD_KEY) {
    return `¿Qué ${field.label.toLocaleLowerCase('es-MX')} buscas?`
  }
  const label = field.label.trim()
  switch (field.dataType) {
    case 'boolean':
      return `¿Confirmas “${label}”? Responde sí o no.`
    case 'date':
      return `¿Qué fecha pongo para “${label}”?`
    case 'number':
      return `¿Qué cantidad pongo para “${label}”?`
    default:
      return `¿Me compartes “${label}”?`
  }
}

/** La frase con la que el flujo pregunta un dato: la CONFIGURADA si la hay, la
 *  generada si no.
 *
 *  La regla vive aquí, en una función, porque la leen el plan (lo que se
 *  publica) y la pantalla (lo que se promete). En blanco cae a la generada: "lo
 *  dejé vacío" y "usa la tuya" son un solo estado. */
export function askPromptForField(field: FichaField): string {
  return field.prompt?.trim() || promptForField(field)
}

/** Las claves que UNA etapa declara, en el orden de la ficha.
 *
 *  Lo que la etapa OBTIENE más lo que EXIGE: obtiene es la narrativa del proceso
 *  ("esto se aprende aquí") y exigir sin preguntar es una captura que
 *  `capturar_lead` rechaza por datos faltantes.
 *
 *  LA CAÍDA A LA FICHA COMPLETA YA NO ES DE ETAPA, ES DE PROCESO (F3-1b). Una
 *  etapa que no declara nada, dentro de un proceso donde otra sí declara, no
 *  tiene que preguntar la ficha entera: preguntar todo en cada columna era
 *  repetirle al prospecto lo que ya contestó. La caída sigue existiendo — un
 *  proceso cuyas columnas no cuentan su historia todavía tiene una ficha que sí
 *  — pero la decide `planCaptureFlow` mirando el proceso COMPLETO, y este
 *  segundo parámetro es lo que le deja pedirla explícitamente. */
export function questionKeysFor(
  input: {
    ficha: readonly FichaField[]
    requiredFields: readonly string[]
    gatheredFields: readonly string[]
  },
  wholeFichaWhenSilent = true,
): string[] {
  const declared = new Set([...input.gatheredFields, ...input.requiredFields])
  const inFicha = input.ficha.map((f) => f.key)
  const picked = inFicha.filter((k) => declared.has(k))
  if (picked.length > 0) return picked
  return wholeFichaWhenSilent ? inFicha : []
}

/** El orden TOTAL del compilador: `position`, y la clave para desempatar. Es el
 *  mismo criterio que `orderedStages` de compile.ts, escrito aquí porque
 *  `compile.ts` importa este archivo y la otra dirección sería un ciclo. */
function byStageOrder(a: CaptureFlowPlanStage, b: CaptureFlowPlanStage): number {
  return a.position - b.position || (a.key < b.key ? -1 : a.key > b.key ? 1 : 0)
}

/** Las etapas donde la captura puede preguntar algo: desde la primera columna
 *  hasta la primera que TERMINA la conversación (traspaso o fin), sin incluirla.
 *  Después de un traspaso ya no hay agente preguntando, así que una columna de
 *  captura que vive más allá no es un tramo: es trabajo de una persona. */
function captureCandidates<T extends CaptureFlowPlanStage>(sorted: readonly T[]): T[] {
  const stop = sorted.findIndex((s) => s.behavior === 'handoff' || s.behavior === 'fin')
  const windowed = stop === -1 ? sorted : sorted.slice(0, stop)
  // LA ETAPA QUE AGENDA TAMBIÉN ES CANDIDATA (D22-6). Hasta aquí este filtro la
  // saltaba EN SILENCIO: una columna de `agenda` dentro de la ventana no era un
  // tramo, así que el proceso compilaba como si no estuviera y nadie reservaba.
  // Pregunta lo que su columna declara igual que cualquier otra; lo que agrega
  // es lo que pasa DESPUÉS de preguntar.
  const candidates = windowed.filter((s) => s.behavior === 'captura' || s.behavior === 'agenda')
  if (candidates.length > 0) return candidates
  // UNA sola etapa = la eligió una persona en la pantalla (o la eligió el
  // detector de deriva leyendo el flujo vivo). Esa ES la captura, lleve el
  // behavior que lleve su columna: negárselo dejaría la pantalla sin plan.
  return sorted.length === 1 ? [...sorted] : []
}

/** Qué columnas del proceso COMPILA su captura, y si se cae a la ficha completa.
 *
 *  UNA sola regla, y por eso está aquí afuera del plan: el emisor la necesita
 *  para saber hasta dónde llega la captura, y la vista previa para decir a dónde
 *  termina el proceso (`terminalOfProcess`). Escrita dos veces, la pantalla
 *  podría prometer una terminal que el agente no corre, que es exactamente la
 *  deriva que este ciclo existe para terminar.
 *
 *  Genérica en la etapa porque el compilador la llama con sus `CompilerStage`,
 *  que son la MISMA forma con dos campos más: copiar el tipo para poder llamarla
 *  sería copiar la regla. */
export function selectCaptureTramos<T extends CaptureFlowPlanStage>(
  stages: readonly T[],
  ficha: readonly FichaField[],
): { tramos: T[]; wholeFichaFallback: boolean; agendaStage: T | null } {
  const sorted = [...stages].sort(byStageOrder)
  const candidates = captureCandidates(sorted)
  if (candidates.length === 0) return { tramos: [], wholeFichaFallback: false, agendaStage: null }
  const fichaKeys = new Set(ficha.map((f) => f.key))
  const declaresSomething = (s: T): boolean =>
    [...s.gatheredFields, ...s.requiredFields].some((k) => fichaKeys.has(k))
  // Hasta dónde llega la captura: el ÚLTIMO tramo que declara algo. Las columnas
  // de captura posteriores no aportan preguntas, así que llevar la tarjeta hasta
  // ellas sería adelantarla sin haber aprendido nada.
  //
  // UNA ETAPA DE `agenda` SIEMPRE CUENTA COMO DECLARANTE (D22-6): aunque su
  // columna no nombre un solo dato, lo que hace ahí el Agente es RESERVAR, así
  // que recortarla por "no aporta preguntas" habría compilado el proceso sin la
  // única cosa que esa columna existe para hacer.
  const lastDeclaring = candidates.reduce(
    (acc, s, i) => (declaresSomething(s) || s.behavior === 'agenda' ? i : acc),
    -1,
  )
  // La CAÍDA A LA FICHA COMPLETA sigue mirando sólo lo que las columnas declaran
  // de la ficha: un proceso que no cuenta su historia la recibe aunque tenga una
  // columna de agenda — y entonces no hay con qué reservar, que es lo que
  // `planCaptureFlow` rechaza por su nombre.
  const wholeFichaFallback = !candidates.some(declaresSomething)
  // Y LA CAPTURA TERMINA DONDE SE AGENDA. Reservar es el final del trabajo del
  // Agente en este proceso: lo que venga después de esa columna lo hace alguien
  // más, y además es lo que deja al `guardar` del cierre bloqueado siendo el
  // puente que mueve la tarjeta con la cita.
  const agendaAt = candidates.findIndex((s) => s.behavior === 'agenda')
  const end = agendaAt === -1 ? lastDeclaring : agendaAt
  return {
    tramos: wholeFichaFallback ? [candidates[0]!] : candidates.slice(0, end + 1),
    wholeFichaFallback,
    // La columna que agenda, esté o no entre los tramos: con la caída a la ficha
    // completa NO lo está, y ése es justo el caso que el plan tiene que poder
    // rechazar por su nombre (un proceso que quiere agendar sin decir qué).
    agendaStage: agendaAt === -1 ? null : candidates[agendaAt]!,
  }
}

/** A DÓNDE TERMINA LA RAMA DE UN PROCESO, leído de sus behaviors (D1/D2).
 *
 *  Se mira lo que viene DESPUÉS de la ventana de captura: si alguna columna
 *  posterior es de `handoff`, la conversación acaba con una persona; si alguna
 *  es `fin`, se despide; si no, el bot sigue y la rama cierra en `fin_ok`.
 *
 *  `llenado_manual` no aparece aquí a propósito (D2): esa columna la llena una
 *  PERSONA, así que no emite preguntas ni terminal — pasar por ella no cambia a
 *  dónde va la conversación. `multiplexor` tampoco, ver el TODO de `compile.ts`.
 *  Y `agenda` TAMPOCO (D22-6): ahí es donde se reserva, no a donde la
 *  conversación va después de reservar — esa columna es un TRAMO de la captura,
 *  y la terminal se sigue leyendo en lo que viene detrás de ella.
 *
 *  VIVÍA EN `compile.ts` (F4-6). Bajó aquí porque desde que la terminal es un
 *  control visible la leen TRES lados — el compilador, la generación desde la
 *  pantalla de Procesos y el emisor a través del plan — y `compile.ts` importa
 *  este archivo, así que la otra dirección sería un ciclo. `processCaptureWindow`
 *  y `terminalOfProcess` siguen exportándose desde allá, sobre esta cuenta. */
function terminalFrom(sorted: readonly CaptureFlowPlanStage[], from: number): AgentTerminal {
  for (const s of sorted.slice(from)) {
    if (s.behavior === 'handoff') return 'pasar'
    if (s.behavior === 'fin') return 'cierre'
  }
  return 'fin_ok'
}

/** La VENTANA de captura de un proceso: qué columnas compila su captura, y a
 *  dónde termina su rama una vez recorridas.
 *
 *  UNA SOLA REGLA, y por eso este envoltorio existe (F3-1c). Desde que la
 *  captura se reparte en TRAMOS, la terminal ya no se lee después de la primera
 *  columna de captura sino después de la ÚLTIMA que se compila, y esa elección
 *  la hace `selectCaptureTramos` mirando la ficha.
 *
 *  Sin NINGUNA columna de `captura` no hay tramos y la terminal se lee desde el
 *  principio del proceso, que es lo que el emisor hacía desde siempre. */
export function captureWindowOf<T extends CaptureFlowPlanStage>(
  stages: readonly T[],
  ficha: readonly FichaField[] = [],
): { compiledStages: T[]; terminal: AgentTerminal } {
  const ordered = [...stages].sort(byStageOrder)
  // La caída a UNA etapa suelta de `selectCaptureTramos` es para la pantalla que
  // elige una columna a mano; un proceso compilado sin ninguna `captura` no
  // pregunta nada, así que aquí esa caída no aplica.
  const compiledStages = ordered.some((s) => s.behavior === 'captura' || s.behavior === 'agenda')
    ? selectCaptureTramos(ordered, ficha).tramos
    : []
  const last = compiledStages[compiledStages.length - 1]
  const from = last === undefined ? 0 : ordered.indexOf(last) + 1
  return { compiledStages, terminal: terminalFrom(ordered, from) }
}

/** A dónde termina la rama de ESTE proceso, con las columnas que trae. La misma
 *  cuenta que compila el emisor, para que la pantalla no pueda prometer una
 *  terminal que el Agente no corre. */
export function terminalAfterCapture<T extends CaptureFlowPlanStage>(
  stages: readonly T[],
  ficha: readonly FichaField[] = [],
): AgentTerminal {
  return captureWindowOf(stages, ficha).terminal
}

/** Cómo se EMITE un plan: lo que `buildCaptureFlow` necesita para escribir el
 *  flujo que este plan describe.
 *
 *  UNA sola casa, porque hay DOS emisores del mismo plan: el compilador del
 *  Agente (compile.ts) y la generación desde la pantalla de Procesos
 *  (packages/api/src/routes/me/capture-flow.ts). Escrita dos veces, uno de los
 *  dos publicaría un flujo de otra forma que el otro, que es exactamente la
 *  deriva que este ciclo existe para terminar.
 *
 *  LA CAÍDA A LA FICHA COMPLETA SE EMITE COMO SIEMPRE, byte por byte: un proceso
 *  cuyas columnas no cuentan su historia no gana tramos por haberse
 *  recompilado (lock de Juan). Con columnas que sí declaran, el emisor por
 *  tramos hace que la tarjeta nazca al entrar y avance de columna en columna. */
/** UN PROCESO QUE NO PREGUNTARÍA NADA (F4-7, reescrito en F5-1).
 *
 *  Sólo alcanzable con una ficha sin ningún dato preguntable — el nombre
 *  incluido, que desde F5-1 es uno de ellos. La ruta lo contesta EN PALABRAS en
 *  vez de dejar que un `Error` genérico salga como un 500 con una traza que
 *  nadie puede leer. Es una CLASE y no un mensaje suelto para que la ruta pueda
 *  reconocerlo sin comparar texto.
 *
 *  LA FRASE YA NO HABLA DEL NOMBRE. Decía "…antes de quitar el nombre" porque
 *  el único camino a este estado era el interruptor de F4-7; con el interruptor
 *  ido, ese camino es vaciar la ficha, y nombrar el nombre mandaría a buscar un
 *  control que ya no existe. */
export const NO_QUESTIONS_LEFT_MESSAGE =
  'Este proceso se quedaría sin ninguna pregunta. Agrega al menos un dato a la ficha.'

export class CaptureFlowHasNoQuestionsError extends Error {
  constructor() {
    super('planCaptureFlow: el proceso no pregunta el nombre y su ficha no tiene datos.')
    this.name = 'CaptureFlowHasNoQuestionsError'
  }
}

/** QUÉ LE FALTA A UNA ETAPA DE `agenda` PARA PODER RESERVAR. Dos datos, y los
 *  dos son de la ficha: con qué se busca (la especialidad, que es el interés del
 *  giro) y para cuándo (una fecha). */
export type AgendaMissingDatum = 'especialidad' | 'fecha'

/** Cómo se dice cada faltante, en la palabra del producto. */
const AGENDA_MISSING_LABEL: Record<AgendaMissingDatum, string> = {
  especialidad: 'el dato de interés (la especialidad o servicio que se agenda)',
  fecha: 'un dato de tipo fecha (para cuándo la quiere)',
}

export function agendaNeedsDataMessage(
  stageName: string,
  missing: readonly AgendaMissingDatum[],
): string {
  const falta = missing.map((m) => AGENDA_MISSING_LABEL[m])
  const lista =
    falta.length === 1 ? falta[0]! : `${falta.slice(0, -1).join(', ')} y ${falta.at(-1)!}`
  return (
    `La etapa “${stageName}” agenda, pero el proceso nunca pregunta ${lista}. ` +
    'Agrega ese dato a la ficha y declara en qué columna se obtiene.'
  )
}

/** UN PROCESO QUE QUIERE AGENDAR Y NO DIJO QUÉ (D22-6).
 *
 *  Una etapa de `agenda` reserva contra la agenda REAL, y para eso hacen falta
 *  dos respuestas: qué se agenda y para cuándo. Sin alguna de las dos no hay
 *  reserva posible, y compilar "casi" la reserva sería lo peor de los dos
 *  mundos: el agente prometería una cita que no puede buscar. Así que el proceso
 *  compila SIN reserva — sus preguntas siguen funcionando, la vista previa no
 *  truena — y esto queda anotado para que la puerta de publicación lo pueda
 *  rechazar con la causa en la mano.
 *
 *  Es una CLASE, como `CaptureFlowHasNoQuestionsError`, para que quien la atrapa
 *  no tenga que comparar texto. */
export class AgendaStageNeedsDataError extends Error {
  readonly stageKey: string
  readonly stageName: string
  readonly missing: readonly AgendaMissingDatum[]
  constructor(stage: { key: string; name: string }, missing: readonly AgendaMissingDatum[]) {
    super(agendaNeedsDataMessage(stage.name, missing))
    this.name = 'AgendaStageNeedsDataError'
    this.stageKey = stage.key
    this.stageName = stage.name
    this.missing = missing
  }
}

// ── LA PREGUNTA DICE DE DÓNDE SALE SU LISTA (D21-3) ────────────────────────
//
// Las dos frases viven AQUÍ, en el compilador, y no en cada pantalla, por lo
// mismo que el mapa de tipos: el recibo del Publicar y la vista previa dicen el
// MISMO hecho, y dos redacciones del mismo hecho en dos pantallas del mismo
// producto se leen como dos productos. El cliente ve la lista viva en el
// diálogo del dato (D21-1); aquí la ve en las palabras del agente.

/** Cómo se nombra cada fuente dentro de una frase. */
const SOURCE_NAME: Record<Exclude<FieldOptionsSource, 'lista'>, string> = {
  agenda: 'tu Agenda',
  catalogo: 'tu Catálogo',
}

/** Cuántas opciones se citan antes de resumir. Un catálogo de cuarenta
 *  artículos convertiría un renglón del recibo en una página, y el recibo se
 *  lee de un vistazo o no se lee. */
const SOURCE_VALUES_SHOWN = 6

function sourceValuesList(values: readonly string[]): string {
  if (values.length <= SOURCE_VALUES_SHOWN) return values.join(', ')
  const shown = values.slice(0, SOURCE_VALUES_SHOWN).join(', ')
  const rest = values.length - SOURCE_VALUES_SHOWN
  return `${shown} y ${rest} más`
}

/**
 * LA CLÁUSULA DE UN DATO CON FUENTE: de dónde toma su lista y qué hay hoy en
 * ella. Encaja dentro de una frase ("El dato "Especialidad" …"), por eso empieza
 * en verbo y no lleva punto.
 *
 * `values` son las opciones HORNEADAS, que son las de hoy en el momento en que
 * quien llama las leyó. "hoy" no es una promesa de que mañana digan lo mismo:
 * es exactamente lo contrario, y es lo que esta cláusula viene a decir.
 */
export function sourcedOptionsClause(
  source: Exclude<FieldOptionsSource, 'lista'>,
  values: readonly string[],
): string {
  return `acepta las opciones de ${SOURCE_NAME[source]} (hoy: ${sourceValuesList(values)})`
}

/** LA MISMA NOTA, donde la pregunta ya dice de qué dato es (la vista previa la
 *  cuelga de la pregunta, con su etiqueta encima): repetir ahí "El dato
 *  “Especialidad”" sería decir dos veces lo mismo a dos centímetros. */
export function sourcedOptionsNote(
  source: Exclude<FieldOptionsSource, 'lista'>,
  values: readonly string[],
): string {
  const clause = sourcedOptionsClause(source, values)
  return `${clause.charAt(0).toUpperCase()}${clause.slice(1)}.`
}

/** El renglón del recibo para UN dato con fuente. */
export function sourcedFieldFact(
  label: string,
  source: Exclude<FieldOptionsSource, 'lista'>,
  values: readonly string[],
): string {
  return `El dato “${label}” ${sourcedOptionsClause(source, values)}.`
}

/** UNA FUENTE VACÍA AL PUBLICAR. La degradación de `askExtractForDataType`
 *  deja la pregunta como texto libre, que es una conversación que funciona con
 *  peores datos. Se dice, en la vista previa, donde la pregunta se nombra: es
 *  una NOTA y nunca un impedimento — el proceso publica igual. */
export function emptySourceNote(source: Exclude<FieldOptionsSource, 'lista'>): string {
  return (
    `Este dato toma su lista de ${SOURCE_NAME[source]} y hoy no hay opciones, ` +
    'así que se pregunta como texto libre.'
  )
}

/** La fuente de un dato de la ficha, o `null` cuando escribe su propia lista.
 *  Una función y no un `!== 'lista'` repetido en cada sitio, porque los tres
 *  valores que significan "sin fuente" (ausente, `null` y `lista`) son
 *  exactamente donde una copia se olvida de uno. */
export function fieldSourceOf(
  field: Pick<FichaField, 'optionsSource'>,
): Exclude<FieldOptionsSource, 'lista'> | null {
  const s = field.optionsSource
  if (s === undefined || s === null || s === 'lista') return null
  return s
}

export function captureFlowOptionsOf(plan: CaptureFlowPlan): CaptureFlowOptions {
  return {
    questions: plan.questions,
    // EXPLÍCITO, nunca por posición: ver `CaptureFlowOptions.nameVar`.
    nameVar: plan.nameVar,
    funnel: plan.funnel,
    stage: plan.stage,
    confirmText: plan.confirmText,
    // A dónde termina el proceso (F4-6). Viaja en el plan para que el flujo que
    // se emite y la arista de regreso que lo espera salgan de la MISMA cuenta.
    terminal: plan.terminal,
    ...(plan.wholeFichaFallback
      ? {}
      : {
          stages: plan.stages.map((s) => ({
            stage: s.stageKey,
            idSuffix: s.stageKey,
            questionIndexes: s.questionIndexes,
            // QUÉ CORRE EN EL TRAMO (D22-6), y con qué agenda cuando agenda. El
            // bloque va ausente en todo tramo de captura, que es lo que deja al
            // emisor escribiendo byte por byte lo de siempre.
            kind: s.kind,
            ...(s.agenda ? { agenda: s.agenda } : {}),
          })),
        }),
  }
}

/** El plan completo, calculado una vez y usado por la vista previa Y por la
 *  escritura, para que la frase que el cliente confirma no pueda mentir.
 *
 *  POR ETAPAS (F3-1b). Un proceso reparte sus datos entre columnas: la
 *  especialidad se sabe en una, la fecha tentativa en la siguiente, la
 *  confirmación en la de más allá. El plan sigue esa repartición — un TRAMO por
 *  columna — en vez de preguntar todo de un jalón y aterrizar la tarjeta en una
 *  sola etapa. Lo que NO cambia es la lista plana de preguntas: la vista previa,
 *  la fusión y el detector de deriva la leen entera, y los tramos la indexan. */
export function planCaptureFlow(input: CaptureFlowPlanInput): CaptureFlowPlan {
  const {
    tramos: tramoStages,
    wholeFichaFallback,
    agendaStage,
  } = selectCaptureTramos(input.stages, input.ficha)
  if (tramoStages.length === 0) {
    throw new Error('planCaptureFlow: el proceso no tiene ninguna etapa de captura que compilar.')
  }

  const questions: CaptureFlowQuestion[] = []
  const questionLabels: string[] = []
  const asked = new Set<string>()
  const fieldByKey = new Map(input.ficha.map((f) => [f.key, f]))

  // EL NOMBRE QUE NADIE COLOCÓ ABRE LA CAPTURA (F5-1).
  //
  // Una columna dice qué OBTIENE, y desde F5-1 puede nombrar `nombre` como
  // nombra cualquier dato: ahí se pregunta y ahí se pregunta nada más. Eso es lo
  // que Juan pidió poder elegir. Pero las columnas que YA existen no lo nombran
  // — el nombre no era un dato cuando se escribieron —, así que leerlas al pie
  // de la letra habría dejado a todo proceso por tramos SIN la pregunta del
  // nombre, en silencio, el día de la migración. No es una conducta que nadie
  // eligió: es una lista que se escribió antes de que hubiera algo que elegir.
  //
  // Así que un `nombre` que la ficha lleva y NINGUNA columna declara conserva su
  // lugar de siempre: abre el primer tramo que pregunta algo, que es exactamente
  // donde `planCaptureFlow` lo ponía hasta ayer. Declararlo en una columna es lo
  // que lo mueve. Es la misma clase de regla que `wholeFichaFallback` — el
  // proceso que no cuenta su historia recibe la conducta por omisión, no el
  // vacío — y por eso vive junto a ella y no en la migración: escrita en SQL
  // habría sido una lista de columnas tocada una vez, y el proceso creado mañana
  // con columnas declarantes habría vuelto a perder la pregunta.
  const nameUndeclared =
    !wholeFichaFallback &&
    input.ficha.some((f) => f.key === NAME_FIELD_KEY) &&
    !tramoStages.some((s) => [...s.gatheredFields, ...s.requiredFields].includes(NAME_FIELD_KEY))
  let namePlaced = !nameUndeclared

  const stages: CaptureStagePlan[] = tramoStages.map((stage) => {
    let keys = wholeFichaFallback
      ? questionKeysFor({ ficha: input.ficha, requiredFields: [], gatheredFields: [] })
      : questionKeysFor(
          {
            ficha: input.ficha,
            requiredFields: stage.requiredFields,
            gatheredFields: stage.gatheredFields,
          },
          false,
        )
    // El primer tramo que pregunta algo. Colgarlo de uno que sólo adelanta la
    // tarjeta dejaría la pregunta en un paso que no pregunta.
    if (!namePlaced && keys.some((k) => !asked.has(k))) {
      keys = [NAME_FIELD_KEY, ...keys]
      namePlaced = true
    }
    const questionIndexes: number[] = []
    for (const key of keys) {
      if (asked.has(key)) continue
      const field = fieldByKey.get(key)
      if (!field) continue
      asked.add(key)
      questionIndexes.push(questions.length)
      questions.push({
        var: field.key,
        // La frase es del CLIENTE cuando la escribió, y de fábrica cuando no.
        prompt: askPromptForField(field),
        // La ficha ya dice qué ES el dato; el ask dice cómo se INTERPRETA la
        // respuesta. La traducción vive en UNA tabla compartida que este
        // generador y el selector del editor consumen juntos (C2).
        // Y LA FUENTE VA CON ELLA (D21-3): sin esto la pregunta publicada se
        // queda con la lista del día que se publicó, y el médico que se dio de
        // alta el martes no existe para el agente hasta el siguiente Publicar.
        extract: askExtractForDataType({
          data_type: field.dataType,
          options: field.options,
          options_source: field.optionsSource,
        }),
        ...(field.key === INTEREST_FIELD_KEY ? { interest: true } : {}),
        // CUÁL PREGUNTA BAUTIZA LA TARJETA (F5-1), marcada igual que el
        // interés y por la misma razón: su respuesta viaja en un argumento
        // propio de `capturar_lead` y NUNCA dentro de `campos`. De esta marca
        // sale `plan.nameVar`, que es lo que el emisor lee.
        ...(field.key === NAME_FIELD_KEY ? { name: true } : {}),
      })
      questionLabels.push(field.label)
    }
    return {
      stageKey: stage.key,
      stageName: stage.name,
      questionIndexes,
      // El NOMBRE ya no se filtra de aquí (F5-1): una columna puede exigirlo
      // como exige cualquier otro dato, y desde que es un renglón de la ficha
      // `capturar_lead` lo cuenta como presente igual que a los demás.
      requiredKeys: input.ficha.map((f) => f.key).filter((k) => stage.requiredFields.includes(k)),
      // QUÉ CORRE EN EL TRAMO (D22-6), leído del behavior de su columna y no de
      // su posición: es `agenda` o es captura, no hay tercera.
      kind: stage.behavior === 'agenda' ? ('agenda' as const) : ('captura' as const),
    }
  })

  // Sin una sola pregunta no hay captura que armar. Desde F5-1 el nombre es un
  // dato más de la ficha, así que este caso es UNO solo y no dos: una ficha sin
  // ningún dato preguntable no tiene NADA que preguntar, y eso se dice aquí para
  // que la ruta lo conteste en palabras en vez de dejar que el emisor truene con
  // un error que nadie puede leer. Va ANTES de resolver la agenda: un proceso
  // mudo no está al que le falte la fecha, está sin ficha.
  if (questions.length === 0) {
    throw new CaptureFlowHasNoQuestionsError()
  }

  // ── LA ETAPA QUE AGENDA, RESUELTA POR CONVENCIÓN (D22-6) ────────────────────
  //
  // Reservar contra la agenda REAL necesita dos respuestas, y las dos ya están
  // en la ficha de cualquier proceso que agende: QUÉ se agenda y PARA CUÁNDO.
  // Se resuelven por convención y no por configuración porque el cliente ya lo
  // dijo una vez — al armar su ficha y al declarar qué columna obtiene cada
  // dato — y pedírselo otra vez en un formulario aparte sería el mismo hecho
  // con dos casas, que es la deriva que este ciclo existe para terminar.
  // Un proceso que no declara NADA y quiere agendar no ha dicho qué agenda: la
  // ficha entera se pregunta en la primera columna, y ninguna columna dice cuál
  // de esos datos es el horario. La columna de agenda ni siquiera llegó a ser un
  // tramo, así que se lee de la ventana.
  if (wholeFichaFallback && agendaStage !== null) {
    throw new AgendaStageNeedsDataError(agendaStage, ['especialidad', 'fecha'])
  }
  const agendaTramoIndex = tramoStages.findIndex((s) => s.behavior === 'agenda')
  if (agendaTramoIndex !== -1) {
    const stage = tramoStages[agendaTramoIndex]!
    const declared = new Set([...stage.gatheredFields, ...stage.requiredFields])
    const fieldOf = (q: CaptureFlowQuestion): FichaField | undefined => fieldByKey.get(q.var)
    // LA ESPECIALIDAD ES LA PREGUNTA MARCADA COMO EL INTERÉS, no una clave
    // literal: el cliente puede renombrar la etiqueta del dato ("Servicio",
    // "Área") y la marca sigue siendo la misma, porque es lo que viaja en la
    // COLUMNA de interés de la tarjeta.
    const specialtyVar = questions.find((q) => q.interest)?.var
    // Y EL CUÁNDO ES LA FECHA QUE LA COLUMNA QUE AGENDA DECLARA. Si no declara
    // ninguna, la ÚLTIMA fecha que el proceso preguntó antes: quien dio una
    // fecha en una columna anterior ya dijo para cuándo la quiere, y volver a
    // preguntársela sería el agente sin memoria.
    const isDate = (q: CaptureFlowQuestion): boolean => fieldOf(q)?.dataType === 'date'
    const whenVar =
      questions.find((q) => isDate(q) && declared.has(q.var))?.var ??
      [...questions].reverse().find(isDate)?.var
    const missing: AgendaMissingDatum[] = [
      ...(specialtyVar === undefined ? (['especialidad'] as const) : []),
      ...(whenVar === undefined ? (['fecha'] as const) : []),
    ]
    if (missing.length > 0) throw new AgendaStageNeedsDataError(stage, missing)
    // La toma de datos de la visita: sólo lo que este proceso YA pregunta.
    const askedVar = (key: string): string | undefined => questions.find((q) => q.var === key)?.var
    const intake = {
      ...(askedVar(AGENDA_INTAKE_KEYS.reason) ? { reason: AGENDA_INTAKE_KEYS.reason } : {}),
      ...(askedVar(AGENDA_INTAKE_KEYS.firstVisit)
        ? { firstVisit: AGENDA_INTAKE_KEYS.firstVisit }
        : {}),
      ...(askedVar(AGENDA_INTAKE_KEYS.insurer) ? { insurer: AGENDA_INTAKE_KEYS.insurer } : {}),
    }
    stages[agendaTramoIndex]!.agenda = { specialtyVar: specialtyVar!, whenVar: whenVar!, intake }
  }

  const first = stages[0]!
  const landing = stages[stages.length - 1]!
  // A dónde termina el proceso (F4-6), de la MISMA cuenta que el compilador usa
  // para la arista de salida del `call`: las dos se leen juntas o la
  // conversación vuelve a un nodo que no dice nada.
  const terminal = captureWindowOf(input.stages, input.ficha).terminal
  const optionLabel = input.entryLabel ?? captureCategoryLabelFor(input.pipelineName)
  const meaning = input.meaning ?? null
  const asksName = questions.some((q) => q.name)
  return {
    flowName: captureFlowNameFor(input.pipelineName).slice(0, FLOW_NAME_MAX),
    optionKey: input.pipelineKey,
    // La CONFIGURADA cuando el cliente escribió una, y la derivada cuando no.
    // Es la MISMA coalescencia que el compilador hace para armar el menú, sobre
    // el MISMO valor ya resuelto: dos maneras de elegir la etiqueta serían dos
    // etiquetas, y la opción del menú y el flujo que llama dejarían de ser la
    // misma rama.
    optionLabel,
    // EL SIGNIFICADO (F5-2). Con significado escrito, el clasificador lo lee
    // además de la etiqueta; sin él lee la etiqueta sola, que es lo único que
    // leía antes y por lo tanto la única caída honesta. Derivar aquí una frase
    // florida ("El cliente pregunta por Urgencias, sus servicios y sus
    // precios") sería el sistema inventándole al agente un significado que
    // nadie escribió.
    meaning,
    callNodeId: `llamar_${input.pipelineKey}`,
    questions,
    // Quién es el nombre, dicho y no deducido (F4-7). Desde F5-1 sale de la
    // pregunta MARCADA y no de una bandera del proceso: el nombre está donde la
    // ficha lo puso, así que la única manera honesta de saber si se pregunta —
    // y cuál pregunta es — es mirar las preguntas que salieron. `null` cuando la
    // ficha no lleva el dato, que es el mismo estado que `asks_name = false`
    // significaba antes.
    nameVar: questions.find((q) => q.name)?.var ?? null,
    questionLabels,
    funnel: input.pipelineKey,
    stage: first.stageKey,
    stageName: first.stageName,
    stages,
    landingStage: landing.stageKey,
    landingStageName: landing.stageName,
    wholeFichaFallback,
    pipelineName: input.pipelineName,
    terminal,
    // D22-6: UN PROCESO QUE AGENDA CONFIRMA LA CITA, no la lista de datos. Lo
    // que la persona necesita poder desmentir es el horario y el folio; leerle
    // de vuelta "Especialidad: Cardiología" después de haberle agendado sería
    // confirmar lo de menos. La otra frase — la cita apartada — la escribe el
    // emisor con la misma función, porque es un nodo suyo.
    //
    // R4: la confirmación de una captura LEE DE VUELTA lo capturado.
    // `questions` y `questionLabels` van en el mismo orden por construcción (se
    // empujan juntas arriba), así que la lista sale en el orden en que se
    // preguntó. La última frase sigue a la TERMINAL (F4-6): un proceso que se
    // despide no puede prometer un traspaso que nadie va a hacer.
    confirmText:
      agendaTramoIndex !== -1
        ? generatedAgendaConfirm('firme', terminal, asksName)
        : generatedCaptureConfirm(
            questions
              .map((q, i) => ({ variable: q.var, label: questionLabels[i] ?? q.var }))
              // UN BOOLEANO NO SE LEE DE VUELTA (F6-4). La confirmación existe
              // para que el prospecto revise lo que se guardó, y "Cita
              // Confirmada: sí" repite lo que la frase entera ya está diciendo:
              // es ruido en una lista que se lee de un vistazo. En la
              // transcripción de Juan salió peor todavía, como "Cita
              // Confirmada: true". El dato SÍ se guarda: sale del ECO, no de
              // `capturar_lead`.
              .filter((e) => fieldByKey.get(e.variable)?.dataType !== 'boolean'),
            terminal,
            // Sin pregunta de nombre la confirmación abre sin él: la variable no
            // existe y `renderText` la dejaría en blanco ("Listo, ."). Lo dice
            // la ficha, no una bandera (F5-1).
            asksName,
          ),
  }
}
